import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { dateOf, splitWordsOnUpperCase } from "@repo/helpers";
import { KeyedArray } from "@repo/models";
import { Col, Loading, Row } from "@repo/ui";
import { ChevronDown } from "lucide-react";
import { FC, useCallback, useMemo } from "react";

import { useRepDisciplineQueries } from "../../api/repDisciplineApi";
import { useSegmentContext } from "../../contexts/segmentContext";
import { useUpdateRoute } from "../../helpers/urlHooks";
import { RepDiscipline } from "../../models";
import { tabs } from "../../string";
import style from "./style.module.scss";

interface Props {
	lastDisciplineName: string;
	possibleBlacklist: boolean;
}

export const Discipline: FC<Props> = (props) => {
	const { lastDisciplineName, possibleBlacklist } = props;
	const { segmentId } = useSegmentContext();
	const { useRepDisciplineQuery } = useRepDisciplineQueries();
	const { data, loaded } = useRepDisciplineQuery();

	const disciplines = useMemo(() => {
		if (!data) return new KeyedArray<RepDiscipline>([], (d) => d.repDisciplineId);
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		data.data.forEach((rd) => {
			rd.startDate && rd.startDate.setHours(0, 0, 0, 0);
			if (rd.endDate) rd.endDate.setHours(0, 0, 0, 0);
		});
		return data
			.filter((rd) => !segmentId || !rd.segmentId || rd.segmentId.includes(segmentId))
			.filter((rd) => !rd.startDate || rd.startDate < today || rd.startDate.getTime() === today.getTime())
			.filter((rd) => !rd.endDate || rd.endDate > today);
	}, [data, segmentId]);

	if (!disciplines.any()) return null;
	if (!possibleBlacklist && !lastDisciplineName) return null;

	if (possibleBlacklist || disciplines.count() !== 1) {
		return (
			<Row tag="section">
				<Col md="12">
					<Accordion elevation={0}>
						<AccordionSummary
							expandIcon={<ChevronDown />}
							aria-controls="rep-discipline-content"
							id="rep-discipline-header"
						>
							<h3 className={style.red}>
								{possibleBlacklist ? (
									<span className={style.marginRight}>Agent Possibly Blocklisted</span>
								) : null}
								<span>{splitWordsOnUpperCase(lastDisciplineName)}</span>
							</h3>
						</AccordionSummary>
						<AccordionDetails>
							<Loading loading={!loaded} message="Loading Disciplines...">
								{disciplines.map((d) => (
									<DisciplineDetail discipline={d} key={d.repDisciplineId} />
								))}
							</Loading>
						</AccordionDetails>
					</Accordion>
				</Col>
			</Row>
		);
	}

	return (
		<Row tag="section">
			<Col md="12">
				<h3 className={style.red}>
					<DisciplineDetail discipline={disciplines.data[0]} />
				</h3>
			</Col>
		</Row>
	);
};

const DisciplineDetail: FC<SingleProps> = ({ discipline }) => {
	const updateRoute = useUpdateRoute();
	const onClick = useCallback(() => {
		updateRoute(discipline.repDisciplineId, tabs.repDisciplines);
	}, [updateRoute, discipline]);
	const type = splitWordsOnUpperCase(discipline.typeName);
	const featureName = splitWordsOnUpperCase(discipline.feature?.name);
	const featureText = featureName && `(${featureName})`;
	return (
		<p key={discipline.repDisciplineId} onClick={onClick} className={style.singleDiscipline}>
			<b>
				{type} {featureText}{" "}
			</b>
			<span>{discipline.reasonName} </span>
			<span>
				{getDateString(discipline.startDate)} - {getDateString(discipline.endDate)}
			</span>
		</p>
	);

	function getDateString(date: Date) {
		return date ? dateOf(date) : "TBD";
	}
};

interface SingleProps {
	discipline: RepDiscipline;
}
